import { DateTime } from 'luxon';

export const DEFAULT_TIMEZONE = 'Europe/Paris';
export const HOME_TIMEZONE = DEFAULT_TIMEZONE; // 홈 스튜디오 타임존 별칭
export const DEFAULT_LOCALE = 'fr-FR';

// 지원 타임존 목록 (유럽, 아시아, 북미, 남미)
export const TIMEZONE_OPTIONS = [
  // 유럽
  { value: 'Europe/Paris', label: 'Paris (UTC+1)' },
  { value: 'Europe/London', label: 'London (UTC+0)' },
  { value: 'Europe/Berlin', label: 'Berlin (UTC+1)' },
  { value: 'Europe/Madrid', label: 'Madrid (UTC+1)' },
  { value: 'Europe/Rome', label: 'Rome (UTC+1)' },
  { value: 'Europe/Amsterdam', label: 'Amsterdam (UTC+1)' },
  { value: 'Europe/Brussels', label: 'Brussels (UTC+1)' },
  { value: 'Europe/Vienna', label: 'Vienna (UTC+1)' },
  { value: 'Europe/Zurich', label: 'Zurich (UTC+1)' },
  { value: 'Europe/Stockholm', label: 'Stockholm (UTC+1)' },
  { value: 'Europe/Copenhagen', label: 'Copenhagen (UTC+1)' },
  { value: 'Europe/Oslo', label: 'Oslo (UTC+1)' },
  { value: 'Europe/Helsinki', label: 'Helsinki (UTC+2)' },
  { value: 'Europe/Warsaw', label: 'Warsaw (UTC+1)' },
  { value: 'Europe/Prague', label: 'Prague (UTC+1)' },
  { value: 'Europe/Lisbon', label: 'Lisbon (UTC+0)' },
  { value: 'Europe/Athens', label: 'Athens (UTC+2)' },
  // 아시아
  { value: 'Asia/Seoul', label: 'Seoul (UTC+9)' },
  { value: 'Asia/Tokyo', label: 'Tokyo (UTC+9)' },
  { value: 'Asia/Shanghai', label: 'Shanghai (UTC+8)' },
  { value: 'Asia/Hong_Kong', label: 'Hong Kong (UTC+8)' },
  { value: 'Asia/Singapore', label: 'Singapore (UTC+8)' },
  { value: 'Asia/Taipei', label: 'Taipei (UTC+8)' },
  { value: 'Asia/Bangkok', label: 'Bangkok (UTC+7)' },
  { value: 'Asia/Ho_Chi_Minh', label: 'Ho Chi Minh (UTC+7)' },
  { value: 'Asia/Jakarta', label: 'Jakarta (UTC+7)' },
  { value: 'Asia/Manila', label: 'Manila (UTC+8)' },
  { value: 'Asia/Kuala_Lumpur', label: 'Kuala Lumpur (UTC+8)' },
  // 북미
  { value: 'America/New_York', label: 'New York (UTC-5)' },
  { value: 'America/Los_Angeles', label: 'Los Angeles (UTC-8)' },
  { value: 'America/Chicago', label: 'Chicago (UTC-6)' },
  { value: 'America/Denver', label: 'Denver (UTC-7)' },
  { value: 'America/Phoenix', label: 'Phoenix (UTC-7)' },
  { value: 'America/Toronto', label: 'Toronto (UTC-5)' },
  { value: 'America/Vancouver', label: 'Vancouver (UTC-8)' },
  { value: 'America/Montreal', label: 'Montreal (UTC-5)' },
  { value: 'America/Mexico_City', label: 'Mexico City (UTC-6)' },
  // 남미
  { value: 'America/Sao_Paulo', label: 'São Paulo (UTC-3)' },
  { value: 'America/Buenos_Aires', label: 'Buenos Aires (UTC-3)' },
  { value: 'America/Santiago', label: 'Santiago (UTC-4)' },
  { value: 'America/Lima', label: 'Lima (UTC-5)' },
  { value: 'America/Bogota', label: 'Bogotá (UTC-5)' },
];

// Date → 파리 ISO 문자열
export function dateToParisISOString(date) {
  return DateTime.fromJSDate(date).setZone(DEFAULT_TIMEZONE).toISO();
}

// 파리 Date → UTC ISO 문자열
export function parisDateToUTCISOString(date) {
  return DateTime.fromJSDate(date, { zone: DEFAULT_TIMEZONE }).toUTC().toISO();
}

// UTC ISO → 파리 Date 객체
export function utcISOToParisDate(isoString) {
  return DateTime.fromISO(isoString, { zone: 'utc' }).setZone(DEFAULT_TIMEZONE).toJSDate();
}

// 파리 Date → 표시용 문자열
export function parisDateToDisplay(date, locale = DEFAULT_LOCALE) {
  return DateTime.fromJSDate(date).setZone(DEFAULT_TIMEZONE).setLocale(locale).toLocaleString(DateTime.DATETIME_MED);
}

// FullCalendar 시간 포맷 상수
export const FULLCALENDAR_TIME_FORMAT = {
  hour: '2-digit',
  minute: '2-digit',
  hour12: false
};
export const FULLCALENDAR_SLOT_LABEL_FORMAT = { hour: 'numeric', hour12: false };

// FullCalendar 이벤트 변환 (시간대 일관성 적용)
export function toCalendarEvent(e) {
  if (!e || !e.start || !e.end) return null;
  // start, end는 UTC ISO 문자열 또는 Date 객체 허용
  let startDate = e.start instanceof Date ? e.start : new Date(e.start_time || e.start);
  let endDate = e.end instanceof Date ? e.end : new Date(e.end_time || e.end);
  if (!(startDate instanceof Date) || isNaN(startDate.getTime())) return null;
  if (!(endDate instanceof Date) || isNaN(endDate.getTime())) return null;
  if (startDate >= endDate) return null;
  const safeColor = typeof e.user_color === 'string' && /^#[0-9A-F]{6}$/i.test(e.user_color) ? e.user_color : '#1976d2';
  const baseEvent = {
    id: e.id || `event-${Date.now()}-${Math.random()}`,
    title: e.title || 'Untitled Event',
    start: e.start,
    end: e.end,
    allDay: Boolean(e.is_all_day),
    backgroundColor: safeColor,
    borderColor: safeColor,
    extendedProps: {
      type: e.type || 'event',
      createdBy: e.created_by,
      artist_name: e.artist_name,
      client_name: e.client_name,
      project_type: e.project_type,
      reservationId: e.extendedProps?.reservationId || e.id
    }
  };
  if (e.type === 'block') {
    baseEvent.display = 'background';
    baseEvent.backgroundColor = 'rgba(255,0,0,0.18)';
    baseEvent.borderColor = 'rgba(255,0,0,0.18)';
    baseEvent.className = 'blocked-bg-event';
    baseEvent.title = 'BLOCKED';
  } else if (e.type === 'dayoff') {
    baseEvent.display = 'background';
    baseEvent.backgroundColor = 'rgba(80,80,80,0.45)';
    baseEvent.borderColor = 'rgba(80,80,80,0.45)';
    baseEvent.textColor = '#fff';
    baseEvent.fontWeight = 400;
    baseEvent.className = 'dayoff-bg-event';
    baseEvent.title = 'OFF';
  }
  return baseEvent;
}

// createParisDateTimeInfo: FullCalendar에서 파리 시간대로 받은 Date 객체를 포맷팅
export function createParisDateTimeInfo(startDate, endDate, locale = DEFAULT_LOCALE) {
  if (!startDate || !endDate || isNaN(startDate.getTime()) || isNaN(endDate.getTime())) {
    return null;
  }
  try {
    // FullCalendar의 timeZone이 'Europe/Paris'로 설정되어 있으므로,
    // 받은 Date 객체는 이미 파리 시간대입니다.
    // 따라서 추가 변환 없이 그대로 사용합니다.
    console.log('createParisDateTimeInfo - 입력 시간:', {
      startDate: startDate.toLocaleString(),
      endDate: endDate.toLocaleString(),
      startHours: startDate.getHours(),
      endHours: endDate.getHours()
    });

    // FullCalendar에서 이미 파리 시간대로 받은 Date 객체이므로
    // 추가 변환 없이 단순히 로컬 시간으로 표시만 합니다.
    return {
      startDate: startDate.toLocaleDateString(locale, {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric'
      }),
      startTime: startDate.toLocaleTimeString(locale, {
        hour: '2-digit',
        minute: '2-digit',
        hour12: false
      }),
      endTime: endDate.toLocaleTimeString(locale, {
        hour: '2-digit',
        minute: '2-digit',
        hour12: false
      })
    };
  } catch (error) {
    console.error('createParisDateTimeInfo 에러:', error);
    return null;
  }
}

export function createLocalDateTimeInfo(startDate, endDate, locale = DEFAULT_LOCALE) {
  if (!startDate || !endDate || isNaN(startDate.getTime()) || isNaN(endDate.getTime())) {
    return null;
  }
  try {
    // FullCalendar에서 이미 파리 시간대로 받은 Date 객체이므로
    // 추가 변환 없이 단순히 로컬 시간으로 표시만 합니다.
    return {
      startDate: startDate.toLocaleDateString(locale, {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric'
      }),
      startTime: startDate.toLocaleTimeString(locale, {
        hour: '2-digit',
        minute: '2-digit',
        hour12: false
      }),
      endTime: endDate.toLocaleTimeString(locale, {
        hour: '2-digit',
        minute: '2-digit',
        hour12: false
      })
    };
  } catch (error) {
    console.error('createLocalDateTimeInfo 에러:', error);
    return null;
  }
}

export function formatDateTimeRange(startTime, endTime, locale = DEFAULT_LOCALE) {
  if (!startTime || !endTime) return '';
  try {
    const start = utcISOToParisDate(startTime);
    const end = utcISOToParisDate(endTime);
    return `${parisDateToDisplay(start, locale)} ~ ${parisDateToDisplay(end, locale)}`;
  } catch (e) {
    return '';
  }
}

export function getLocaleByLang(lang) {
  switch (lang) {
    case 'ko': return 'ko-KR';
    case 'fr': return 'fr-FR';
    case 'en': return 'en-GB';
    default: return DEFAULT_LOCALE;
  }
}

export function getCalendarLocale() {
  const lang = (navigator.language || navigator.userLanguage || '').toLowerCase();
  if (lang.startsWith('ko')) return 'ko';
  if (lang.startsWith('fr')) return 'fr';
  return 'en';
}

export function formatLocalToUTC(dateStr, timeStr) {
  if (!dateStr || !timeStr) {
    return null;
  }
  try {
    // 날짜 형식 변환: "15/07/2025" → "2025-07-15"
    let formattedDate;
    if (dateStr.includes('/')) {
      const dateParts = dateStr.split('/');
      if (dateParts.length === 3) {
        const day = dateParts[0].padStart(2, '0');
        const month = dateParts[1].padStart(2, '0');
        const year = dateParts[2];
        formattedDate = `${year}-${month}-${day}`;
      } else {
        throw new Error('날짜 형식이 올바르지 않습니다.');
      }
    } else {
      // 기존 "2025. 07. 15." 형식 처리
      formattedDate = dateStr
        .replace(/\./g, '-')
        .replace(/\s/g, '')
        .replace(/-$/, '');
    }

    // Luxon을 사용해 파리 시간대 DateTime 생성 후 UTC ISO로 변환
    const dt = DateTime.fromISO(`${formattedDate}T${timeStr}`, { zone: DEFAULT_TIMEZONE });
    if (!dt.isValid) throw new Error('Luxon DateTime 생성 실패');
    return dt.toUTC().toISO();
  } catch (error) {
    console.error('formatLocalToUTC 에러:', error);
    return null;
  }
}

export function formatUTCToLocalDate(isoString, locale = DEFAULT_LOCALE) {
  if (!isoString || isoString === 'null' || isoString.trim() === '') {
    return '';
  }
  try {
    const date = utcISOToParisDate(isoString);
    return date.toLocaleDateString(locale, {
      day: '2-digit',
      month: '2-digit',
      year: '2-digit'
    });
  } catch (error) {
    console.error('formatUTCToLocalDate 에러:', error);
    return '';
  }
}

export function formatUTCToLocalDateSlash(isoString, locale = DEFAULT_LOCALE) {
  if (!isoString || isoString === 'null' || isoString.trim() === '') {
    return '';
  }
  try {
    const date = utcISOToParisDate(isoString);
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = String(date.getFullYear()).slice(-2); // 2자리 년도
    return `${day}/${month}/${year}`;
  } catch (error) {
    console.error('formatUTCToLocalDateSlash 에러:', error);
    return '';
  }
}

export function getTimezoneConfig() {
  return {
    timezone: DEFAULT_TIMEZONE,
    locale: DEFAULT_LOCALE
  };
}

// 기타 필요한 시간대 변환/포맷 함수도 여기에 추가 가능 